const BASE = process.env.REACT_APP_API_URL || '/api';

// For direct browser navigation (new tabs), we need the absolute backend URL.
// In dev, CRA proxy only works for XHR — direct navigation must hit the backend directly.
export function getDirectApiUrl(path) {
  // If REACT_APP_API_URL is set and absolute, use it
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.startsWith('http')) {
    return `${process.env.REACT_APP_API_URL}${path}`;
  }
  // In dev (CRA on port 3000), proxy target is port 5000
  if (window.location.port === '3000') {
    return `${window.location.protocol}//${window.location.hostname}:5000/api${path}`;
  }
  // In production, same origin serves both
  return `/api${path}`;
}

// ─── Refresh token management ───
let _isRefreshing = false;
let _refreshQueue = [];

function _processRefreshQueue(newToken) {
  _refreshQueue.forEach(cb => cb(newToken));
  _refreshQueue = [];
}

async function _tryRefresh() {
  const refreshToken = localStorage.getItem('zeus_refresh_token');
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    localStorage.setItem('zeus_token', data.token);
    localStorage.setItem('zeus_refresh_token', data.refresh_token);
    localStorage.setItem('zeus_user', JSON.stringify(data.user));
    return data.token;
  } catch { return null; }
}

// ─── Core request helper ───
async function request(method, path, body, options = {}) {
  const token = localStorage.getItem('zeus_token');
  const headers = {};

  if (token) headers['Authorization'] = `Bearer ${token}`;

  const config = { method, headers };

  if (body instanceof FormData) {
    config.body = body;
  } else if (body) {
    headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(body);
  }

  let res = await fetch(`${BASE}${path}`, config);

  // On 401 — try refresh before logging out
  if (res.status === 401 && !options._isRetry) {
    if (!_isRefreshing) {
      _isRefreshing = true;
      const newToken = await _tryRefresh();
      _isRefreshing = false;

      if (newToken) {
        _processRefreshQueue(newToken);
        // Retry the original request with new token
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryConfig = { method, headers };
        if (body instanceof FormData) {
          retryConfig.body = body;
        } else if (body) {
          retryConfig.body = JSON.stringify(body);
        }
        res = await fetch(`${BASE}${path}`, retryConfig);
      } else {
        _processRefreshQueue(null);
        localStorage.removeItem('zeus_token');
        localStorage.removeItem('zeus_refresh_token');
        localStorage.removeItem('zeus_user');
        if (window.name !== 'zeuscms-messenger') window.location.href = '/login';
        throw new Error('Session expired');
      }
    } else {
      // Another request is already refreshing — wait for it
      const newToken = await new Promise(resolve => _refreshQueue.push(resolve));
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryConfig = { method, headers };
        if (body instanceof FormData) {
          retryConfig.body = body;
        } else if (body) {
          retryConfig.body = JSON.stringify(body);
        }
        res = await fetch(`${BASE}${path}`, retryConfig);
      } else {
        throw new Error('Session expired');
      }
    }
  }

  // Final 401 check after retry
  if (res.status === 401) {
    localStorage.removeItem('zeus_token');
    localStorage.removeItem('zeus_refresh_token');
    localStorage.removeItem('zeus_user');
    if (window.name !== 'zeuscms-messenger') window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: 'Request failed' }));
    const err = new Error(errBody.error || errBody.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = errBody;
    throw err;
  }

  return res.json();
}

// ─── Generic REST methods ───
const api = {
  get:    (path)        => request('GET',    path),
  post:   (path, body)  => request('POST',   path, body),
  put:    (path, body)  => request('PUT',    path, body),
  patch:  (path, body)  => request('PATCH',  path, body),
  delete: (path, body)   => request('DELETE', path, body),
};

// Strip undefined/null/empty from query params
const qs = (params) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
  );
  const s = new URLSearchParams(clean).toString();
  return s ? `?${s}` : '';
};

// ═══════════════════════════════════════════
// Auth
// ═══════════════════════════════════════════
export const loginUser = (email, password) => api.post('/auth/login', { email, password });
export const registerUser = (data) => api.post('/auth/register', data);

// ═══════════════════════════════════════════
// Dashboard
// ═══════════════════════════════════════════
export const getDashboard = () => api.get('/dashboard');

// ═══════════════════════════════════════════
// Cases
// ═══════════════════════════════════════════
export const getCases = (params = {}) => api.get(`/cases${qs(params)}`);
export const getCase = (id) => api.get(`/cases/${id}`);
export const checkDuplicate = (name, dob, doi, ssn) => {
  const params = { name };
  if (dob) params.dob = dob;
  if (doi) params.doi = doi;
  if (ssn) params.ssn = ssn;
  return api.get(`/cases/check-duplicate${qs(params)}`);
};
export const createCase = async (data) => {
  try {
    return await api.post('/cases', data);
  } catch (err) {
    if (err.status === 409 && err.data?.error === 'duplicate_found') {
      const dupErr = new Error(err.data.message || 'Duplicate case found');
      dupErr.isDuplicate = true;
      dupErr.duplicates = err.data.duplicates || [];
      throw dupErr;
    }
    throw err;
  }
};
export const updateCase = (id, data) => api.put(`/cases/${id}`, data);
export const addCaseNote = (caseId, content, note_type, color) => api.post(`/cases/${caseId}/notes`, { content, note_type, color });
export const updateCaseNote = (caseId, noteId, content, note_type, color) => api.put(`/cases/${caseId}/notes/${noteId}`, { content, note_type, color });
export const addTreatment          = (caseId, data)          => api.post(`/cases/${caseId}/treatments`, data);
export const runManualWorkflow     = (caseId, workflowId)    => api.post(`/cases/${caseId}/run-workflow/${workflowId}`);
export const addConsultation       = (caseId, data)          => api.post(`/cases/${caseId}/consultations`, data);
export const patchConsultation     = (caseId, consultId, data) => api.patch(`/cases/${caseId}/consultations/${consultId}`, data);
export const deleteConsultation    = (caseId, consultId)     => api.delete(`/cases/${caseId}/consultations/${consultId}`);
export const getAttorneys = () => api.get('/cases/lookup/attorneys');
export const getStaff = () => api.get('/cases/lookup/staff');

export const uploadCaseDocument = async (caseId, formData) => {
  const token = localStorage.getItem('zeus_token');
  const res = await fetch(`${BASE}/cases/${caseId}/documents`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) throw new Error('Upload failed');
  return res.json();
};

// Document trash operations
export const softDeleteDocument = (caseId, docId) => api.put(`/cases/${caseId}/documents/${docId}/delete`);
export const restoreDocument = (caseId, docId) => api.put(`/cases/${caseId}/documents/${docId}/restore`);
export const permanentDeleteDocument = (caseId, docId) => api.delete(`/cases/${caseId}/documents/${docId}/permanent`);
export const purgeDocumentTrash = (caseId) => api.post(`/cases/${caseId}/documents/purge-trash`);

export const upsertCaseFieldNote = (caseId, source_field, content) =>
  api.put(`/cases/${caseId}/field-notes`, { source_field, content });

// ═══════════════════════════════════════════
// Clients
// ═══════════════════════════════════════════
export const getClients = (params = {}) => api.get(`/clients${qs(params)}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);

// ═══════════════════════════════════════════
// Tasks
// ═══════════════════════════════════════════
export const getTasks        = (params = {}) => api.get(`/tasks${qs(params)}`);
export const getTaskPendingCount = ()           => api.get('/tasks/my-pending-count');

export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// ═══════════════════════════════════════════
// Events
// ═══════════════════════════════════════════
export const getEvents    = (params = {}) => api.get(`/events${qs(params)}`);
export const createEvent  = (data)        => api.post('/events', data);
export const updateEvent  = (id, data)    => api.put(`/events/${id}`, data);
export const deleteEvent  = (id)          => api.delete(`/events/${id}`);

// ═══════════════════════════════════════════
// Invoices / Billing
// ═══════════════════════════════════════════
export const getInvoices = (params = {}) => api.get(`/invoices${qs(params)}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const recordPayment = (invoiceId, amount) => api.post(`/invoices/${invoiceId}/payments`, { amount });
export const getBillingStats = () => api.get('/invoices/stats');

// ═══════════════════════════════════════════
// Users / Team
// ═══════════════════════════════════════════
export const getUsers = () => api.get('/users');

// ═══════════════════════════════════════════
// Care Providers / Network
// ═══════════════════════════════════════════
export const getCareProviders = (params = {}) => api.get(`/care-providers${qs(params)}`);
export const createCareProvider = (data) => api.post('/care-providers', data);
export const updateCareProvider = (id, data) => api.put(`/care-providers/${id}`, data);
export const deleteCareProvider = (id, confirm_name) => api.delete(`/care-providers/${id}`, { confirm_name });
export const getAuditLogs    = (params = {}) => api.get(`/users/audit-logs${qs(params)}`);
export const getGoogleMapsKey = ()            => api.get('/users/google-maps-key');

// ═══════════════════════════════════════════
// Messenger
// ═══════════════════════════════════════════
export const getConversations = (params = {}) => api.get(`/messages/conversations${qs(params)}`);
export const getUnreadCount = () => api.get('/messages/unread-count');
export const createConversation = (data) => api.post('/messages/conversations', data);
export const getMessages = (convId, params = {}) => api.get(`/messages/conversations/${convId}/messages${qs(params)}`);
export const sendMessage = (convId, content) => api.post(`/messages/conversations/${convId}/messages`, { content });
export const markConversationRead = (convId) => api.put(`/messages/conversations/${convId}/read`);
export const updateConversation = (convId, data) => api.put(`/messages/conversations/${convId}`, data);
export const leaveConversation = (convId) => api.post(`/messages/conversations/${convId}/leave`);
export const archiveConversation = (convId) => api.post(`/messages/conversations/${convId}/archive`);
export const unarchiveConversation = (convId) => api.post(`/messages/conversations/${convId}/unarchive`);
export const deleteConversation = (convId) => api.delete(`/messages/conversations/${convId}`);
export const transferConversationAdmin = (convId, newAdminId) => api.post(`/messages/conversations/${convId}/transfer-admin`, { new_admin_id: newAdminId });
export const exportConversation = async (convId) => {
  const BASE = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('zeus_token') || '';
  const res = await fetch(`${BASE}/messages/conversations/${convId}/export`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Export failed');
  const text = await res.text();
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `chat-export-${convId.slice(0, 8)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

// ═══════════════════════════════════════════
// Document Templates
// ═══════════════════════════════════════════
export const getMergeFields = () => api.get('/templates/merge-fields');
export const getTemplates = (params = {}) => api.get(`/templates${qs(params)}`);
export const getTemplate = (id) => api.get(`/templates/${id}`);
export const createTemplate = (data) => api.post('/templates', data);
export const updateTemplate = (id, data) => api.put(`/templates/${id}`, data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`);
export const duplicateTemplate = (id) => api.post(`/templates/${id}/duplicate`);
export const generateDocument = (templateId, data) => api.post(`/templates/${templateId}/generate`, data);
export const generatePdfDocument = (templateId, data) => api.post(`/templates/${templateId}/generate-pdf`, data);
export const getPdfFields = (templateId) => api.get(`/templates/${templateId}/pdf-fields`);
export const savePdfMapping = (templateId, mapping) => api.put(`/templates/${templateId}/pdf-mapping`, { mapping });
export const uploadPdfTemplate = async (file, name, category) => {
  const token = localStorage.getItem('zeus_token');
  const fd = new FormData();
  fd.append('file', file);
  if (name) fd.append('name', name);
  if (category) fd.append('category', category);
  const res = await fetch(`${BASE}/templates/upload-pdf`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Upload failed'); }
  return res.json();
};
export const getGeneratedDocuments = (params = {}) => api.get(`/templates/documents/generated${qs(params)}`);
export const getGeneratedDocument = (id) => api.get(`/templates/documents/generated/${id}`);
export const importDocxTemplate = async (file, category) => {
  const token = localStorage.getItem('zeus_token');
  const fd = new FormData();
  fd.append('file', file);
  if (category) fd.append('category', category);
  const resp = await fetch('/api/templates/import-docx', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: fd,
  });
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.error || 'Import failed'); }
  return resp.json();
};

// Profile
export const updateProfile = (data) => api.put('/auth/profile', data);
export const changePassword = (data) => api.put('/auth/password', data);
export const uploadAvatar = async (file) => {
  const token = localStorage.getItem('zeus_token');
  const formData = new FormData();
  formData.append('avatar', file);
  // Use BASE (REACT_APP_API_URL) so this hits api.zeuscms.com not the frontend domain
  const res = await fetch(`${BASE}/auth/avatar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to upload avatar');
  }
  return res.json();
};

export const uploadLogo = async (file) => {
  const token = localStorage.getItem('zeus_token');
  const formData = new FormData();
  formData.append('logo', file);
  const res = await fetch('/api/users/organization/logo', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Upload failed');
  }
  return res.json();
};

export const removeLogo = () => api.delete('/users/organization/logo');

export default api;

// Custom Lists (Settings)
export const getCustomLists = () => api.get('/users/custom-lists');
export const updateCustomList = (list_name, items, layout_map, sol_rules) => api.put('/users/custom-lists', { list_name, items, layout_map, sol_rules });
export const updateProviderServices = (servicesMap) => api.put('/users/custom-lists', { list_name: 'provider_services', items: servicesMap });

// Super Admin user management
export const resetUserPassword = (userId, new_password) => api.put(`/users/${userId}/reset-password`, { new_password });
export const pauseUser = (userId) => api.put(`/users/${userId}/pause`);
export const unpauseUser = (userId) => api.put(`/users/${userId}/unpause`);
export const changeAccessLevel = (userId, access_level) => api.put(`/users/${userId}/access-level`, { access_level });

// Case Deletion
export const deleteCase = (id, reason, confirm_name) => api.delete(`/cases/${id}`, { reason, confirm_name });
export const getDeletionRequests = () => api.get('/cases/deletion-requests/pending');
export const approveDeletion = (id) => api.put(`/cases/deletion-requests/${id}/approve`);
export const denyDeletion = (id) => api.put(`/cases/deletion-requests/${id}/deny`);

// Workflows
export const getWorkflows = () => api.get('/workflows');
export const getWorkflow = (id) => api.get(`/workflows/${id}`);
export const createWorkflow = (data) => api.post('/workflows', data);
export const updateWorkflow = (id, data) => api.put(`/workflows/${id}`, data);
export const deleteWorkflow = (id) => api.delete(`/workflows/${id}`);
export const toggleWorkflow = (id) => api.put(`/workflows/${id}/toggle`);
export const duplicateWorkflow = (id) => api.post(`/workflows/${id}/duplicate`);

// Intakes
export const getIntakes = (params = {}) => api.get(`/intakes${qs(params)}`);
export const getIntakeActiveCount = ()   => api.get('/intakes/active-count');
export const getIntake = (id) => api.get(`/intakes/${id}`);
export const createIntake = (data) => api.post('/intakes', data);
export const updateIntake = (id, data) => api.put(`/intakes/${id}`, data);
export const deleteIntake = (id) => api.delete(`/intakes/${id}`);
export const convertIntake = (id, data = {}) => api.post(`/intakes/${id}/convert`, data);

// Forms
export const getForms = () => api.get('/forms');
export const getForm = (id) => api.get(`/forms/${id}`);
export const createForm = (data) => api.post('/forms', data);
export const updateForm = (id, data) => api.put(`/forms/${id}`, data);
export const deleteForm = (id) => api.delete(`/forms/${id}`);
export const publishForm = (id) => api.post(`/forms/${id}/publish`);
export const unpublishForm = (id) => api.post(`/forms/${id}/unpublish`);
export const duplicateForm = (id) => api.post(`/forms/${id}/duplicate`);
export const getFormSubmissions = (formId) => api.get(`/forms/${formId}/submissions`);
export const updateSubmissionStatus = (subId, status) => api.put(`/forms/submissions/${subId}/status`, { status });

// Public form (no auth)
export const getPublicForm = async (token) => {
  const resp = await fetch(`/api/forms/public/${token}`);
  if (!resp.ok) throw new Error('Form not found');
  return resp.json();
};
export const submitPublicForm = async (token, data) => {
  const resp = await fetch(`/api/forms/public/${token}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.error || 'Submission failed'); }
  return resp.json();
};

// ── Super Admin ───────────────────────────────────────────────────────────────
export const getAdminStats  = ()          => api.get('/admin/stats');
export const getAdminOrgs   = ()          => api.get('/admin/organizations');
export const getAdminOrg    = (id)        => api.get(`/admin/organizations/${id}`);
export const updateAdminOrg = (id, data)  => api.put(`/admin/organizations/${id}`, data);
export const suspendOrg     = (id)        => api.post(`/admin/organizations/${id}/suspend`);
export const activateOrg    = (id)        => api.post(`/admin/organizations/${id}/activate`);

// ── Support Tickets ───────────────────────────────────────────────────────────
export const getTickets      = (params = {}) => { const q = new URLSearchParams(params).toString(); return api.get(`/support${q ? `?${q}` : ''}`); };
export const getTicket       = (id)          => api.get(`/support/${id}`);
export const createTicket    = (data)        => api.post('/support', data);
export const replyToTicket   = (id, body)    => api.post(`/support/${id}/reply`, { body });

// Global search
export const globalSearch = (q) => api.get(`/search?q=${encodeURIComponent(q)}`);

// Case Billing
export const getCaseBilling      = (caseId)           => api.get(`/billing/case/${caseId}`);
export const addBillingItem      = (table, data)       => api.post(`/billing/${table}`, data);
export const updateBillingItem   = (table, id, data)   => api.put(`/billing/${table}/${id}`, data);
export const deleteBillingItem   = (table, id)         => api.delete(`/billing/${table}/${id}`);

// ═══════════════════════════════════════
// Communications
// ═══════════════════════════════════════
export const getCommunications      = (params = {}) => api.get(`/communications${qs(params)}`);
export const getCommunicationStats  = ()            => api.get('/communications/stats');
export const getCommunicationFollowUps = ()         => api.get('/communications/follow-ups');
export const createCommunication    = (data)        => api.post('/communications', data);
export const updateCommunication    = (id, data)    => api.put(`/communications/${id}`, data);
export const deleteCommunication    = (id)          => api.delete(`/communications/${id}`);
export const renameDocument = (caseId, docId, name) => api.patch(`/cases/${caseId}/documents/${docId}`, { name });

// WC Billing
export const getWcBilling       = (caseId)           => api.get(`/billing/wc/${caseId}`);
export const addWcItem          = (table, data)       => api.post(`/billing/wc/${table}`, data);
export const updateWcItem       = (table, id, data)   => api.put(`/billing/wc/${table}/${id}`, data);
export const deleteWcItem       = (table, id)         => api.delete(`/billing/wc/${table}/${id}`);
export const getBillingSummary = () => api.get('/billing/summary');

// Session Management
export const getSessions       = ()   => api.get('/auth/sessions');
export const revokeSessionById = (id) => api.delete(`/auth/sessions/${id}`);
export const revokeAllSessions = ()   => api.delete('/auth/sessions/all');
export const logoutSession     = ()   => {
  const rt = localStorage.getItem('zeus_refresh_token');
  return api.post('/auth/logout', { refresh_token: rt });
};

// ═══════════════════════════════════════
// DocuSeal (Document Signing)
// ═══════════════════════════════════════
export const getSigningStatus     = ()                => api.get('/signing/status');
export const getSigningRequests   = (caseId)          => api.get(`/signing/case/${caseId}`);
export const createSigningRequest = (data)            => api.post('/signing/create', data);
export const sendSigningRequest   = (id)              => api.post(`/signing/${id}/send`);
export const refreshSigningStatus = (id)              => api.get(`/signing/${id}/refresh`);
export const deleteSigningRequest = (id)              => api.delete(`/signing/${id}`);

// ═══════════════════════════════════════
// Civil Cases
// ═══════════════════════════════════════
export const getCivilDetails      = (caseId)          => api.get(`/civil/${caseId}/details`);
export const saveCivilDetails     = (caseId, data)    => api.put(`/civil/${caseId}/details`, data);
export const getCivilBilling      = (caseId)          => api.get(`/civil/${caseId}/billing`);
export const createCivilBilling   = (caseId, data)    => api.post(`/civil/${caseId}/billing`, data);
export const updateCivilBilling   = (caseId, id, d)   => api.put(`/civil/${caseId}/billing/${id}`, d);
export const deleteCivilBilling   = (caseId, id)      => api.delete(`/civil/${caseId}/billing/${id}`);
export const getCivilCosts        = (caseId)          => api.get(`/civil/${caseId}/costs`);
export const createCivilCost      = (caseId, data)    => api.post(`/civil/${caseId}/costs`, data);
export const updateCivilCost      = (caseId, id, d)   => api.put(`/civil/${caseId}/costs/${id}`, d);
export const deleteCivilCost      = (caseId, id)      => api.delete(`/civil/${caseId}/costs/${id}`);
